import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { PRODUCTS } from '../data/products';
import { reveal, registerScrollTrigger } from '../lib/motion';

const NoteGroup = ({ label, value, delay }) => {
  const ref = useRef(null);

  useEffect(() => {
    registerScrollTrigger();
    // Scoped to this element so the ScrollTrigger dies with it. Without the
    // context these leaked on every dropdown change — the parent remounts all
    // three groups per selection, orphaning their triggers on detached nodes.
    const ctx = gsap.context(() => {
      reveal(ref.current,
        { opacity: 0, y: 22 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out', delay,
          scrollTrigger: { trigger: ref.current, start: 'top 85%', toggleActions: 'play none none none' } }
      );
    }, ref);
    return () => ctx.revert();
  }, [delay, value]);

  return (
    <div ref={ref} style={{ opacity: 0 }}>
      <div style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '3.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '12px' }}>
        {label}
      </div>
      <div style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '19px', color: 'var(--cream)', letterSpacing: '1px', lineHeight: 1.6 }}>
        {value}
      </div>
    </div>
  );
};

const TheScentStory = () => {
  const sectionRef = useRef(null);
  const quoteRef   = useRef(null);
  const titleRef   = useRef(null);
  const [activeId, setActiveId] = useState(4); // Baccarat Noir by default
  const featured = PRODUCTS.find(p => p.id === activeId) || PRODUCTS[3];

  useEffect(() => {
    registerScrollTrigger();
    const ctx = gsap.context(() => {
      reveal(quoteRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1.4, ease: 'power3.out', scrollTrigger: { trigger: quoteRef.current, start: 'top 80%' } }
      );
      reveal(titleRef.current,
        { opacity: 0, x: -40 },
        { opacity: 1, x: 0, duration: 1.2, ease: 'power2.out', scrollTrigger: { trigger: titleRef.current, start: 'top 80%' } }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="story"
      ref={sectionRef}
      data-testid="scent-story-section"
      className="section-pad"
      style={{ background: 'radial-gradient(ellipse at 50% 0%, #2a1a08 0%, var(--deep-warm-black) 45%, var(--black) 100%)', minHeight: '100vh', display: 'flex', alignItems: 'center' }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto', width: '100%' }}>

        <div ref={titleRef} style={{ opacity: 0, marginBottom: '60px' }}>
          <span className="section-eyebrow">03 — Scent Story</span>
        </div>

        {/* Quote */}
        <blockquote
          ref={quoteRef}
          style={{
            fontFamily: 'Cormorant Garamond', fontStyle: 'italic',
            fontSize: 'clamp(24px, 4.5vw, 54px)',
            color: 'var(--cream)', lineHeight: 1.35,
            letterSpacing: '0.5px', fontWeight: 300,
            borderLeft: '2px solid var(--gold)',
            paddingLeft: '36px',
            margin: '0 0 80px 0',
            opacity: 0,
          }}
        >
          "A scent is a memory before it happens —<br />
          <span style={{ color: 'var(--gold-light)' }}>wear it with intention.</span>"
        </blockquote>

        {/* Scent selector dropdown */}
        <div style={{ marginBottom: '52px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <label
            htmlFor="scent-selector"
            style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '3.5px', textTransform: 'uppercase', color: 'var(--gold-dark)', flexShrink: 0 }}
          >
            Select Scent
          </label>
          <div style={{ position: 'relative', flexGrow: 1, maxWidth: '400px' }}>
            <select
              id="scent-selector"
              value={activeId}
              onChange={e => setActiveId(Number(e.target.value))}
              data-testid="scent-selector"
              className="vael-select"
              style={{
                width: '100%',
                background: 'transparent',
                border: '1px solid rgba(201,169,110,0.3)',
                borderRadius: '2px',
                padding: '12px 40px 12px 16px',
                fontFamily: 'Cormorant Garamond',
                fontStyle: 'italic',
                fontSize: '17px',
                color: 'var(--cream)',
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
              }}
            >
              {PRODUCTS.map(p => (
                <option key={p.id} value={p.id} style={{ background: '#1a1510', color: 'var(--cream)' }}>
                  {p.name} — Inspired by {p.inspired_by}
                </option>
              ))}
            </select>
            {/* Dropdown chevron */}
            <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="#C9A96E" strokeWidth="1.2"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Notes grid */}
        <div key={featured.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '40px' }}>
          <NoteGroup label="Top Notes"   value={featured.notes.top}   delay={0}   />
          <NoteGroup label="Heart Notes" value={featured.notes.heart} delay={0.1} />
          <NoteGroup label="Base Notes"  value={featured.notes.base}  delay={0.2} />
        </div>

        {/* Inspired by card */}
        <div style={{ marginTop: '60px', display: 'inline-flex', alignItems: 'center', gap: '20px', padding: '28px 32px', border: '1px solid rgba(201,169,110,0.12)' }}>
          <div style={{ width: '1px', height: '36px', background: 'var(--gold)' }} />
          <div>
            <div style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--gold-dark)', marginBottom: '6px' }}>
              Inspired By
            </div>
            <div style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '19px', color: 'var(--cream)' }}>
              {featured.inspired_by}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default TheScentStory;
